import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { FormsModule, NgForm } from '@angular/forms';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { SendTicketToSORequest } from '../../../core/models/send-ticket-to-so-request.model';
import { SuperOfficeService } from '../../../core/services/super-office.service';
import { TranslateModule } from '@ngx-translate/core';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { DatePipe, NgClass } from '@angular/common';

type SearchType = 'person' | 'company' | 'project';

@Component({
    selector: 'app-super-office-integration',
    templateUrl: './super-office-integration-modal.component.html',
    styleUrls: ['./super-office-integration-modal.component.scss'],
    imports: [
        TranslateModule,
        InlineSVGComponent,
        FormsModule,
        NgClass,
        NgSelectComponent,
        NgOptionTemplateDirective,
        DatePipe,
    ],
})
export class SuperOfficeIntegrationModalComponent implements OnInit, OnDestroy {
    @ViewChild('replyForm') form: NgForm;
    @ViewChild('personSelect') personNgSelect: NgSelectComponent;
    @ViewChild('companyNameSelect') companyNameNgSelect: NgSelectComponent;
    @ViewChild('projectSelect') projectNgSelect: NgSelectComponent;

    confirmCallback?: (emailModel: SendTicketToSORequest) => void;
    onClose: Subject<SendTicketToSORequest> = new Subject();
    ticket;
    ticketTitle: string;

    emailModel: any = {
        isCompleted: true,
        hasAttachments: true,
        availableFor: 'all',
        type: 'email',
        person: null,
        company: null,
    };

    isSubmitted = false;

    loadingPeople = false;
    persons = [];
    searchPersonText: string;

    loadingCompanies = false;
    companies = [];
    searchCompanyText: string;

    loadingProjects = false;
    projects = [];
    searchProjectText: string;

    readonly minSearchLength = 3;
    private searchPersonsQueryUpdate = new Subject<any>();
    private searchCompaniesQueryUpdate = new Subject<any>();
    private searchProjectsQueryUpdate = new Subject<any>();
    private unsubscriber = new Subject<void>();

    constructor(
        public modalRef: BsModalRef,
        private superOfficeService: SuperOfficeService,
    ) {}

    ngOnInit() {
        this.initEmailModel();

        this.searchPersonsQueryUpdate
            .pipe(debounceTime(300), distinctUntilChanged())
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((event) => {
                const companyId = this.emailModel.company && this.emailModel.company.id;
                this.loadPersons(event.term, companyId);
            });

        this.searchCompaniesQueryUpdate
            .pipe(debounceTime(300), distinctUntilChanged())
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((event) => {
                this.loadCompanies(event.term);
            });

        this.searchProjectsQueryUpdate
            .pipe(debounceTime(300), distinctUntilChanged())
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((event) => {
                this.loadProjects(event.term);
            });
    }

    private initEmailModel() {
        this.emailModel.subject = this.ticketTitle;
        this.emailModel.date = new Date();

        const emailTo = this.ticket.emailInfo && this.ticket.emailInfo.to;

        this.loadingCompanies = true;
        this.loadingPeople = true;
        this.superOfficeService
            .getSuggestions(emailTo)
            .pipe(
                finalize(() => {
                    this.loadingPeople = false;
                    this.loadingCompanies = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((data) => {
                if (data.company && data.company.id) {
                    this.emailModel.company = data.company;
                    this.searchCompanyText = data.company.name;

                    this.loadPersons('', this.emailModel.company.id);
                }

                if (data.person && data.person.id) {
                    this.emailModel.person = data.person;
                    this.searchPersonText = data.person.fullName;
                }
            });
    }

    private loadCompanies(searchCompany) {
        this.loadingCompanies = true;
        this.superOfficeService
            .getCompanies(searchCompany)
            .pipe(
                finalize(() => {
                    this.loadingCompanies = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((data) => {
                this.companies = data;
            });
    }

    private loadPersons(searchName, companyId) {
        this.loadingPeople = true;
        this.superOfficeService
            .getPersons(searchName || '', companyId || '')
            .pipe(
                finalize(() => {
                    this.loadingPeople = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((persons) => {
                persons.forEach((person) => {
                    person.fullName = person.fullName || '(no name)';
                });
                this.persons = persons;
            });
    }

    private loadProjects(searchName) {
        this.loadingProjects = true;
        this.superOfficeService
            .getProjects(searchName)
            .pipe(
                finalize(() => {
                    this.loadingProjects = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((data) => {
                this.projects = data;
            });
    }

    private getCompanyInfo(id) {
        this.loadingCompanies = true;
        this.superOfficeService
            .getCompany(id)
            .pipe(
                finalize(() => {
                    this.loadingCompanies = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((data) => {
                this.emailModel.company = data;
            });
    }

    private initPersonsList() {
        if (this.emailModel.company) {
            this.loadPersons('', this.emailModel.company.id);
        } else {
            this.persons = [];
        }
    }

    onSearch(value, type: SearchType) {
        switch (type) {
            case 'person': {
                this.searchPersonText = value.term;

                if (this.searchPersonText && this.searchPersonText.length >= this.minSearchLength) {
                    this.searchPersonsQueryUpdate.next(value);
                } else {
                    this.initPersonsList();
                }
                break;
            }
            case 'company': {
                this.searchCompanyText = value.term;

                if (this.searchCompanyText && this.searchCompanyText.length >= this.minSearchLength) {
                    this.searchCompaniesQueryUpdate.next(value);
                } else {
                    this.companies = [];
                }
                break;
            }
            case 'project': {
                this.searchProjectText = value.term;

                if (this.searchProjectText && this.searchProjectText.length >= this.minSearchLength) {
                    this.searchProjectsQueryUpdate.next(value);
                } else {
                    this.projects = [];
                }
                break;
            }
        }
    }

    clearList(type: SearchType) {
        switch (type) {
            case 'person': {
                this.searchPersonText = '';
                this.initPersonsList();
                break;
            }
            case 'company': {
                this.companies = [];
                this.persons = [];
                this.searchCompanyText = '';
                break;
            }
            case 'project': {
                this.projects = [];
                this.searchProjectText = '';
                break;
            }
        }
    }

    onSelect(value, type: SearchType) {
        switch (type) {
            case 'person': {
                if (value) {
                    if (!this.emailModel.company) {
                        this.getCompanyInfo(value.companyId);
                    }
                }
                break;
            }
            case 'company': {
                if (value) {
                    this.persons.length = 0;
                    this.loadPersons('', this.emailModel.company.id);
                }
                this.emailModel.person = null;
                break;
            }
        }
    }

    decline() {
        this.modalRef.hide();
    }

    save() {
        this.isSubmitted = true;
        if (this.form.valid) {
            this.emailModel.date.setHours(0, 0, 0);

            const emailModel: SendTicketToSORequest = {
                ticketId: this.ticket.id,
                companyId: this.emailModel.company.id,
                personId: (this.emailModel.person && this.emailModel.person.id) || null,
                projectId: (this.emailModel.project && this.emailModel.project.id) || null,
                date: this.emailModel.date.toISOString(),
                type: 'email',
                visibleFor: 'all',
                includeAttachments: true,
                isCompleted: true,
                subject: this.emailModel.subject,
                // token: '',
            };

            if (this.confirmCallback) {
                this.confirmCallback(emailModel);
            }

            this.onClose.next(emailModel);
            this.onClose.complete();

            this.modalRef.hide();
        }
    }

    // todo: what is it? searchStub(term: string, item: any)
    searchStub() {
        return true;
    }

    ngOnDestroy() {
        this.onClose.next(null);
        this.onClose.complete();

        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
